import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sample gallery data for initial display
const SAMPLE_GALLERY = [
  {
    id: 'sample1',
    title: 'Mercedes S-Class Transformation',
    description: 'Full prestige detail including paint correction and ceramic coating',
    before_image: 'https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=800&q=80',
    after_image: 'https://images.unsplash.com/photo-1617531653332-bd46c24f2068?w=800&q=80',
    vehicle_make: 'Mercedes-Benz',
    vehicle_model: 'S-Class',
    service_type: 'Prestige Detail + Ceramic Coating'
  },
  {
    id: 'sample2',
    title: 'BMW M4 Interior Revival',
    description: 'Deep interior clean with leather conditioning',
    before_image: 'https://images.unsplash.com/photo-1603584173870-7f23fdae1b7a?w=800&q=80',
    after_image: 'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=800&q=80',
    vehicle_make: 'BMW',
    vehicle_model: 'M4',
    service_type: 'Prestige Detail'
  },
  {
    id: 'sample3',
    title: 'Range Rover Sport Detail',
    description: 'Complete exterior and interior transformation',
    before_image: 'https://images.unsplash.com/photo-1606016159991-dfe4f2746ad5?w=800&q=80',
    after_image: 'https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?w=800&q=80',
    vehicle_make: 'Land Rover',
    vehicle_model: 'Range Rover Sport',
    service_type: 'Prestige Detail'
  }
];

function BeforeAfterSlider({ beforeImage, afterImage, title }) {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.min(Math.max((x / rect.width) * 100, 0), 100);
    setSliderPosition(percentage);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    handleMove(e.clientX);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    handleMove(e.touches[0].clientX);
  };

  const handleStart = () => setIsDragging(true);
  const handleEnd = () => setIsDragging(false);

  useEffect(() => {
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchend', handleEnd);
    return () => {
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchend', handleEnd);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden cursor-ew-resize select-none"
      onMouseMove={handleMouseMove}
      onTouchMove={handleTouchMove}
      data-testid="before-after-slider"
    >
      {/* After Image (Background) */}
      <img
        src={afterImage}
        alt={`${title} - After`}
        className="absolute inset-0 w-full h-full object-cover"
        draggable={false}
      />
      
      {/* Before Image (Clipped) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${sliderPosition}%` }}
      >
        <img
          src={beforeImage}
          alt={`${title} - Before`}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ width: `${100 / Math.max(sliderPosition / 100, 0.01)}%`, maxWidth: 'none' }}
          draggable={false}
        />
      </div>
      
      {/* Slider Handle */}
      <div
        className="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize z-10"
        style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
        onMouseDown={handleStart}
        onTouchStart={handleStart}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center">
          <ChevronLeft className="w-4 h-4 text-[#1d1d1f] -mr-1" />
          <ChevronRight className="w-4 h-4 text-[#1d1d1f] -ml-1" />
        </div>
      </div>
      
      {/* Labels */}
      <div className="absolute top-4 left-4 bg-black/60 text-white text-xs font-medium px-3 py-1.5 rounded-full">
        Before
      </div>
      <div className="absolute top-4 right-4 bg-[#C41E3A] text-white text-xs font-medium px-3 py-1.5 rounded-full">
        After
      </div>
    </div>
  );
}

export default function Gallery() {
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const response = await fetch(`${API_URL}/api/gallery`);
      if (response.ok) {
        const data = await response.json();
        // Use fetched items or fall back to samples if empty
        setGalleryItems(data.items.length > 0 ? data.items : SAMPLE_GALLERY);
      } else {
        setGalleryItems(SAMPLE_GALLERY);
      }
    } catch (error) {
      console.error('Failed to fetch gallery:', error);
      setGalleryItems(SAMPLE_GALLERY);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center pt-20">
        <Loader2 className="w-8 h-8 text-[#C41E3A] animate-spin" />
      </div>
    );
  }

  return (
    <main data-testid="gallery-page" className="bg-white pt-20 pb-16">
      {/* Header */}
      <section className="py-12 md:py-20 text-center px-6">
        <div className="max-w-[680px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-semibold text-[#1d1d1f] tracking-tight mb-4">
              Our Work.
            </h1>
            <p className="text-lg text-[#C41E3A] font-medium mb-2">
              Before & After Transformations
            </p>
            <p className="text-base text-[#86868b] max-w-md mx-auto">
              Drag the slider to see the difference. Every detail matters.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="px-6 pb-16">
        <div className="max-w-[1000px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {galleryItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group"
                data-testid={`gallery-item-${item.id}`}
              >
                <BeforeAfterSlider
                  beforeImage={item.before_image}
                  afterImage={item.after_image}
                  title={item.title}
                />
                
                <div className="mt-4">
                  <h3 className="text-lg font-semibold text-[#1d1d1f]">{item.title}</h3>
                  {item.vehicle_make && item.vehicle_model && (
                    <p className="text-sm text-[#C41E3A] font-medium">
                      {item.vehicle_make} {item.vehicle_model}
                    </p>
                  )}
                  {item.description && (
                    <p className="text-sm text-[#86868b] mt-1">{item.description}</p>
                  )}
                  {item.service_type && (
                    <span className="inline-block mt-2 text-xs bg-[#f5f5f7] text-[#424245] px-3 py-1 rounded-full">
                      {item.service_type}
                    </span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#1d1d1f]">
        <div className="max-w-[680px] mx-auto text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">
              Ready for your transformation?
            </h2>
            <p className="text-[#c0c0c0] mb-8">
              Book your Prestige Detail today and experience the MA Africa difference.
            </p>
            <a
              href="/booking"
              className="inline-block bg-[#C41E3A] text-white px-8 py-3 rounded-xl font-medium hover:bg-[#a01830] transition-colors"
              data-testid="gallery-cta"
            >
              Book Now
            </a>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
